/**
 * READ ME CAREFULLY BEFORE MAKING CHANGES!
 *
 * These types are what defines the communication between the React Native app
 * and the web app running inside a WebView. The deployment of the web app and
 * the React Native app are independent, so these types must be kept backwards
 * compatible at all times!
 *
 * Most of the data is already marked as partial/optional to allow for future
 * extensions without breaking compatibility but when adding new message types or
 * changing existing ones, make sure they still work with older versions of the
 * app.
 */

import { ServiceLanguage } from "@albertine/i18n";
import type { ExternalLinkPatterns as TenantsJsonExternalLinkPatterns } from "@albertine/tenant-config/schema";
import z from "zod";
import { fetchAccessTokenFromNativeClient } from "./react";

declare global {
    interface Window {
        ReactNativeWebView: {
            postMessage(msg: string): void;
        };
        isReactNativeWebView?: boolean;
        isIosNativeWebView?: boolean;
        isNewAuthEnabled?: boolean;
        /**
         * Set by native iOS/Android SDKs that display their own navigation
         * instead of the Concierge web tab bar. Must not be set by older RN
         * clients that still rely on the web navigation.
         */
        hideConciergeNavigation?: boolean;
        /**
         * Set by native iOS/Android SDKs that own push permission prompts and
         * token registration themselves. Must not be set by already-shipped
         * native SDK builds that still rely on Concierge for that handling.
         */
        disablePushNotificationHandling?: boolean;
        /**
         * Set by native iOS/Android SDKs that create the public share link and
         * present the OS share sheet themselves. Web clients must keep their own
         * share flow when it is absent, so already-shipped native builds (which
         * would silently drop the message) keep working.
         */
        supportsNativeShareLink?: boolean;
        /**
         * Set by native iOS SDK builds that show chat images in their own
         * full-screen viewer. Web clients must keep their own lightbox when it is
         * absent, so already-shipped native builds (which would silently drop the
         * message) keep working.
         */
        supportsNativeImageViewer?: boolean;

        // iOS WKWebView - automatically injected by iOS
        captureNavigateToPostMessage?: boolean;
        webkit?: {
            messageHandlers: {
                // "iosNativeWebView" is the handler name you configure in Swift
                iosNativeWebView: {
                    postMessage(msg: string): void;
                };
                // "iosNativeWebViewWithReply" is async variation of the above that allows receiving a response from the native side
                iosNativeWebViewWithReply: {
                    postMessage(msg: string): unknown;
                };
            };
        };

        // Android WebView - you inject this via addJavascriptInterface
        AndroidBridge?: {
            postMessage(msg: string): void;
            postMessageWithReply?(msg: string, replyId: string): void;
        };

        albertineBridgeReplies?: Record<string, (value: unknown) => void>;
        albertineResolveBridgeReply?: (replyId: string, value: unknown) => void;

        // Generic flag to detect native app context
        isNativeAppWebView?: boolean;
    }
}

export function isEmbeddedNativeWebView(): boolean {
    if (typeof window === "undefined") {
        return false;
    }
    return (
        !!window.isReactNativeWebView ||
        !!window.isIosNativeWebView ||
        !!window.AndroidBridge
    );
}

/**
 * True when the host native SDK creates the share link and presents the OS share
 * sheet itself. The web share flow needs a URL up front, but link creation is a
 * non-idempotent POST, so awaiting it loses the transient user activation that
 * `navigator.share()` requires. Native has no such constraint.
 */
export function supportsNativeShareLink(): boolean {
    return typeof window !== "undefined" && !!window.supportsNativeShareLink;
}

/**
 * True when the host native SDK opens chat images in its own viewer, so the
 * web lightbox, which only covers the WebView, stays closed.
 */
export function supportsNativeImageViewer(): boolean {
    return typeof window !== "undefined" && !!window.supportsNativeImageViewer;
}

export async function getNativeWebViewAccessToken(): Promise<string | null> {
    if (window.isNewAuthEnabled === false) {
        return null;
    }
    return fetchAccessTokenFromNativeClient();
}

export const ExternalLinkPatterns = z.array(
    z.object({
        name: z.string().optional(),
        effect: z.enum(["allow", "block"]),
        action: z
            .enum(["open_browser_modal", "open_link", "notify_unsupported"])
            .nullable()
            .optional(),
        rules: z.array(
            z
                .object({
                    part: z.literal("query"),
                    rule: z.enum(["has", "matches"]),
                    value: z.array(z.string()),
                })
                .or(
                    z.object({
                        part: z.enum(["raw", "host", "path", "scheme"]),
                        rule: z.enum(["equals", "starts_with", "matches"]),
                        value: z.array(z.string()),
                    }),
                ),
        ),
    }),
);

export type ExternalLinkPatterns = z.infer<typeof ExternalLinkPatterns>;

export const ServiceLanguagePreferencePayload = z.object({
    languageCode: ServiceLanguage,
});

// Used to enforce on type level that the manually defined Zod schema matches the
// actual type from the tenant config package.
const _checkExternalLinkPatternsType: ExternalLinkPatterns =
    void 0 as unknown as TenantsJsonExternalLinkPatterns;

export const ReactNativeSafeAreaInsets = z.object({
    top: z.number(),
    bottom: z.number(),
    left: z.number(),
    right: z.number(),
});

export type ReactNativeSafeAreaInsets = z.infer<typeof ReactNativeSafeAreaInsets>;

export const ReactNativePushNotificationsState = z.discriminatedUnion("state", [
    z.object({
        state: z.literal("authorized"),
        token: z.string().optional(),
    }),
    z.object({
        state: z.enum(["denied", "notDetermined"]),
        canAskAgain: z.boolean(),
    }),
]);

export type ReactNativePushNotificationsState = z.infer<
    typeof ReactNativePushNotificationsState
>;

export const ReactNativePushNotificationMessage = z
    .object({
        data: z
            .object({
                conversationId: z.string(),
                messageId: z.string(),
                numberOfAttachments: z.string(),
                request: z.string().or(z.undefined()),
                type: z.string(),
            })
            .partial(),
        from: z.string(),
        messageId: z.string(),
        notification: z
            .object({
                body: z.string(),
                title: z.string(),
                ios: z
                    .object({
                        badge: z.number(),
                    })
                    .partial()
                    .optional(),
                android: z
                    .object({
                        count: z.number(),
                    })
                    .partial()
                    .optional(),
            })
            .partial(),
        appIsForeground: z.boolean(),
    })
    .partial();

export type ReactNativePushNotificationMessage = z.infer<
    typeof ReactNativePushNotificationMessage
>;

export const ReactNativeLocale = z
    .object({
        languageTag: z.string(),
        languageCode: z.string().nullable(),
        languageScriptCode: z.string().nullable(),
        regionCode: z.string().nullable(),
        languageRegionCode: z.string().nullable(),
        currencyCode: z.string().nullable(),
        currencySymbol: z.string().nullable(),
        languageCurrencyCode: z.string().nullable(),
        languageCurrencySymbol: z.string().nullable(),
        decimalSeparator: z.string().nullable(),
        digitGroupingSeparator: z.string().nullable(),
        textDirection: z.enum(["ltr", "rtl"]).nullable(),
        measurementSystem: z.enum(["metric", "us", "uk"]).nullable(),
        temperatureUnit: z.enum(["celsius", "fahrenheit"]).nullable(),
    })
    .partial();

export type ReactNativeLocale = z.infer<typeof ReactNativeLocale>;

export const ReactNativeAppState = z.object({
    deviceOS: z.enum(["ios", "android", "unknown"]),
    deviceVersion: z.string().optional(),
    deviceModelId: z.string().optional().nullable(),
    deviceModelName: z.string().optional().nullable(),
    deviceId: z.string(),
    appVersion: z.string().optional(),
    bundleIdentifier: z.string().optional().nullable(),
    pushNotifications: ReactNativePushNotificationsState,
    initialPushNotification: ReactNativePushNotificationMessage.optional(),
    safeAreaInsets: ReactNativeSafeAreaInsets.optional().nullable(),
    locales: z.array(ReactNativeLocale).min(1).optional(),
});
export type ReactNativeAppState = z.infer<typeof ReactNativeAppState>;

export const ReactNativeStatusBarStyle = z.enum(["auto", "light", "dark", "hidden"]);
export type ReactNativeStatusBarStyle = z.infer<typeof ReactNativeStatusBarStyle>;

export const ReactNativeAppStateUpdate = ReactNativeAppState.partial();
export type ReactNativeAppStateUpdate = z.infer<typeof ReactNativeAppStateUpdate>;

export const AppMessage = z.discriminatedUnion("type", [
    // DEPRECATED: not being used anymore, but kept for backwards compatibility with older versions of the app
    // Called once after receiving the 'webAppInitialized' event from the web app
    // TODO: reactNativeAppStateInitialized is used in AnalyticsClient, so ensure analytics client works as expected when React Native is deprecated
    z.object({
        type: z.literal("reactNativeAppStateInitialized"),
        payload: ReactNativeAppState,
    }),
    z.object({
        type: z.literal("pushNotificationReceived"),
        payload: ReactNativePushNotificationMessage,
    }),
    z.object({
        type: z.literal("pushNotificationsPermissionsResponse"),
        payload: z
            .object({
                deviceId: z.string(),
            })
            .and(
                z.discriminatedUnion("pushNotificationsState", [
                    z.object({
                        pushNotificationsState: z.literal("authorized"),
                        pushNotificationsToken: z.string().optional(),
                    }),
                    z.object({
                        pushNotificationsState: z.enum(["denied", "notDetermined"]),
                    }),
                ]),
            ),
    }),
    z.object({
        type: z.literal("safeAreaInsetsChanged"),
        payload: ReactNativeSafeAreaInsets,
    }),
    z.object({
        type: z.literal("reset_state"),
        payload: z.undefined().optional(),
    }),
]);

export type AppMessage = z.infer<typeof AppMessage>;

export const WebMessage = z.discriminatedUnion("type", [
    // Called once after the web app & react is initialized
    z.object({
        type: z.literal("webAppInitialized"),
        payload: ServiceLanguagePreferencePayload.optional(),
    }),
    z.object({
        type: z.literal("serviceLanguageChanged"),
        payload: ServiceLanguagePreferencePayload,
    }),
    /**
     * @deprecated Native iOS/Android SDKs no longer handle this message.
     * Kept so Concierge and legacy RN clients can still send a valid WebMessage.
     */
    z.object({
        type: z.literal("updateExternalLinkPatterns"),
        payload: ExternalLinkPatterns,
    }),
    z.object({
        type: z.literal("requestPushNotificationPermissions"),
        payload: z.undefined(),
    }),
    z.object({
        type: z.literal("requestLocationPermissions"),
        payload: z.undefined(),
    }),
    z.object({
        type: z.literal("openEmail"),
        payload: z
            .object({
                email: z.string().optional(),
            })
            .optional(),
    }),
    z.object({
        type: z.literal("downloadFile"),
        payload: z.object({
            url: z.url(),
            filename: z.string().optional(),
            mimeType: z.string().optional(),
        }),
    }),
    z.object({
        type: z.literal("setStatusBarStyle"),
        payload: z.object({
            style: ReactNativeStatusBarStyle,
        }),
    }),
    z.object({
        type: z.literal("goToPrimaryLoginStep"),
        payload: z.undefined(),
    }),
    z.object({
        type: z.literal("goToSecondaryLoginStep"),
        payload: z.undefined(),
    }),
    z.object({
        type: z.literal("eligibilitySendCode"),
        payload: z.object({
            phoneNumber: z.string(),
        }),
    }),
    z.object({
        type: z.literal("showSmsIssueBanner"),
        payload: z.object({
            visible: z.boolean(),
        }),
    }),
    z.object({
        type: z.literal("logout"),
        payload: z.undefined(),
    }),
    z.object({
        type: z.literal("navigate"),
        payload: z.object({
            url: z.url(),
        }),
    }),
    z.object({
        type: z.literal("navigateBack"),
        payload: z.undefined(),
    }),
    z.object({
        type: z.literal("startAuthenticationFlow"),
        payload: z.undefined(),
    }),
    z.object({
        type: z.literal("fetchAuthAccessToken"),
        payload: z.undefined(),
    }),
    z.object({
        type: z.literal("keycloakSessionMigration"),
        payload: z.object({
            firebaseToken: z.string().optional(),
        }),
    }),
    /**
     * Asks the native host to mint a public share link for the resource and open
     * the OS share sheet. The web side supplies the localized copy so translations
     * stay in one place; the host supplies the URL.
     */
    z.object({
        type: z.literal("requestShareLink"),
        payload: z.object({
            resourceType: z.enum(["booking", "proposal"]),
            resourceId: z.string().min(1),
            title: z.string(),
            text: z.string(),
        }),
    }),
]);

export type WebMessage = z.infer<typeof WebMessage>;

export const patternMatchesUrl = (
    pattern: ExternalLinkPatterns[number],
    url: URL,
    logger?: Pick<Console, "log" | "warn" | "error">,
): boolean => {
    if (pattern.rules.length === 0) {
        logger?.warn(
            "[patternMatchesUrl] Pattern has no rules, not matching anything.",
        );
        return false;
    }

    return pattern.rules.every((rule) => {
        if (rule.part === "query" && rule.rule === "has") {
            return rule.value.some((param) => url.searchParams.has(param));
        }

        let fieldValue: string;
        switch (rule.part) {
            case "host":
                fieldValue = url.hostname;
                break;
            case "path":
                fieldValue = url.pathname;
                break;
            case "scheme":
                fieldValue = url.protocol;
                break;
            case "raw":
                fieldValue = url.href;
                break;
            case "query":
                fieldValue = url.search;
                break;
            default:
                logger?.error("[patternMatchesUrl] Unknown rule part.", rule);
                return false;
        }

        let matchFn: (fieldValue: string, ruleValue: string) => boolean;
        switch (rule.rule) {
            case "equals":
                matchFn = (fieldValue, ruleValue) => fieldValue === ruleValue;
                break;
            case "starts_with":
                matchFn = (fieldValue, ruleValue) => fieldValue.startsWith(ruleValue);
                break;
            case "matches":
                matchFn = (fieldValue, ruleValue) => {
                    const regex = new RegExp(ruleValue);
                    return regex.test(fieldValue);
                };
                break;
            default:
                logger?.error("[patternMatchesUrl] Unknown rule type.", rule);
                return false;
        }

        return rule.value.some((ruleValue) => matchFn(fieldValue, ruleValue));
    });
};

export function checkUrl(
    {
        baseUrl,
        externalLinkPatterns,
    }: { baseUrl: string; externalLinkPatterns: ExternalLinkPatterns },
    url: string,
    logger?: Pick<Console, "log" | "warn" | "error">,
): Pick<ExternalLinkPatterns[number], "name" | "effect" | "action"> {
    let parsedBaseUrl: URL;
    let parsedUrl: URL;

    try {
        parsedBaseUrl = new URL(baseUrl);
        parsedUrl = new URL(url);
    } catch (error) {
        logger?.error("[checkUrl] Invalid URL.", { baseUrl, url, error });
        return {
            name: "invalid_url",
            effect: "block",
        };
    }

    const isAppUrl = parsedUrl.hostname === parsedBaseUrl.hostname;
    if (isAppUrl) {
        logger?.log("[checkUrl] URL is app URL.", { url });
        return {
            name: "app_url",
            effect: "allow",
            action: null,
        };
    }

    for (const pattern of externalLinkPatterns) {
        logger?.log("[checkUrl] Checking pattern against URL.", { pattern, url });
        if (!patternMatchesUrl(pattern, parsedUrl, logger)) {
            logger?.log("[checkUrl] Pattern did not match URL.", { pattern, url });
            continue;
        }

        logger?.log("[checkUrl] Pattern matched URL.", { pattern, url });
        return {
            name: pattern.name,
            effect: pattern.effect,
            action: pattern.action,
        };
    }

    logger?.log("[checkUrl] No patterns matched URL.", { url });
    return {
        name: "no_match",
        effect: "block",
        action: "open_link",
    };
}
